import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@atoms/Button';
import { Input } from '@atoms/Input';
import { Card } from '@atoms/Card';
import { Spinner } from '@atoms/Spinner';
import { Modal } from '@atoms/Modal';
import { adminService } from '@services/adminService';
import { CategoryIconPicker } from '@molecules/CategoryIconPicker';
import { CategoryIcon, normalizeCategoryIconKey } from '@/utils/categoryIcons';
import type { Category } from '@/types';

export function CategoriesSection() {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [icon, setIcon] = useState('tag');
  const [editCategory, setEditCategory] = useState<Category | null>(null);
  const [editName, setEditName] = useState('');
  const [editIcon, setEditIcon] = useState('tag');

  const { data: categories, isLoading } = useQuery({
    queryKey: ['admin', 'categories'],
    queryFn: () => adminService.getCategories(),
  });

  const createMutation = useMutation({
    mutationFn: () => adminService.createCategory({ name, icon }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'categories'] });
      queryClient.invalidateQueries({ queryKey: ['landing'] });
      setName('');
      setIcon('tag');
    },
  });

  const updateMutation = useMutation({
    mutationFn: () => adminService.updateCategory(editCategory!.id, { name: editName, icon: editIcon }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'categories'] });
      queryClient.invalidateQueries({ queryKey: ['landing'] });
      setEditCategory(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminService.deleteCategory(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'categories'] });
      queryClient.invalidateQueries({ queryKey: ['landing'] });
    },
  });

  if (isLoading) return <Spinner />;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Kategoriler</h1>
        <p className="mt-1 text-sm text-slate-500">Ürün kategorilerini yönetin</p>
      </div>
      <Card className="space-y-3">
        <Input label="Kategori Adı" value={name} onChange={(e) => setName(e.target.value)} />
        <div>
          <p className="mb-2 text-sm font-medium text-gray-700">İkon</p>
          <CategoryIconPicker value={icon} onChange={setIcon} />
        </div>
        <Button loading={createMutation.isPending} onClick={() => createMutation.mutate()}>
          Ekle
        </Button>
      </Card>

      <Modal open={Boolean(editCategory)} onClose={() => setEditCategory(null)} title="Kategori Düzenle">
        <div className="space-y-3">
          <Input label="Kategori Adı" value={editName} onChange={(e) => setEditName(e.target.value)} />
          <CategoryIconPicker value={editIcon} onChange={setEditIcon} />
          <Button loading={updateMutation.isPending} onClick={() => updateMutation.mutate()}>
            Kaydet
          </Button>
        </div>
      </Modal>

      <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-3">
        {(categories ?? []).map((cat) => (
          <Card key={cat.id} className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CategoryIcon name={cat.name} icon={cat.icon} />
              <span className="font-medium">{cat.name}</span>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setEditCategory(cat);
                  setEditName(cat.name);
                  setEditIcon(normalizeCategoryIconKey(cat.icon));
                }}
              >
                Düzenle
              </Button>
              <Button size="sm" variant="danger" onClick={() => deleteMutation.mutate(cat.id)}>
                Sil
              </Button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
